import { useState, useEffect, useCallback, useRef } from 'react';

export type ABVariant = 'A' | 'B' | 'C';

const STORAGE_KEY = 'calculator_variant';
const ANALYTICS_KEY = 'ab_analytics';

export type ABEvent = {
  variant: ABVariant;
  event: string;
  timestamp: number;
  data?: Record<string, unknown>;
};

type AnalyticsData = {
  variant: ABVariant;
  interactions: number;
  timeSpent: number;
  conversions: number;
  bounces: number;
  sessionStart: number;
};

function getStoredVariant(): ABVariant | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'A' || v === 'B' || v === 'C') return v;
  } catch {}
  return null;
}

function pickRandom(): ABVariant {
  const r = Math.random();
  if (r < 0.333) return 'A';
  if (r < 0.666) return 'B';
  return 'C';
}

export function useABTest(forceVariant?: ABVariant) {
  const [variant, setVariant] = useState<ABVariant>(() => {
    if (forceVariant) return forceVariant;
    return getStoredVariant() || pickRandom();
  });

  const analyticsRef = useRef<AnalyticsData>({
    variant,
    interactions: 0,
    timeSpent: 0,
    conversions: 0,
    bounces: 0,
    sessionStart: Date.now(),
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, variant);
    analyticsRef.current.variant = variant;
    analyticsRef.current.sessionStart = Date.now();
  }, [variant]);

  const trackEvent = useCallback((event: string, data?: Record<string, unknown>) => {
    const ev: ABEvent = { variant, event, timestamp: Date.now(), data };
    console.log(`[AB Test] Variant ${variant}:`, event, data || '');

    // Update local analytics
    const a = analyticsRef.current;
    if (event === 'interaction') a.interactions++;
    if (event === 'conversion') a.conversions++;
    if (event === 'bounce') a.bounces++;
    a.timeSpent = Date.now() - a.sessionStart;

    // Persist events
    try {
      const existing = JSON.parse(localStorage.getItem(ANALYTICS_KEY) || '[]') as ABEvent[];
      existing.push(ev);
      // Keep last 200 events
      if (existing.length > 200) existing.splice(0, existing.length - 200);
      localStorage.setItem(ANALYTICS_KEY, JSON.stringify(existing));
    } catch {}
  }, [variant]);

  const setManualVariant = useCallback((v: ABVariant) => {
    setVariant(v);
    localStorage.setItem(STORAGE_KEY, v);
  }, []);

  const getAnalytics = useCallback(() => {
    try {
      const events = JSON.parse(localStorage.getItem(ANALYTICS_KEY) || '[]') as ABEvent[];
      const byVariant: Record<ABVariant, { interactions: number; conversions: number; total: number }> = {
        A: { interactions: 0, conversions: 0, total: 0 },
        B: { interactions: 0, conversions: 0, total: 0 },
        C: { interactions: 0, conversions: 0, total: 0 },
      };
      events.forEach(e => {
        byVariant[e.variant].total++;
        if (e.event === 'interaction') byVariant[e.variant].interactions++;
        if (e.event === 'conversion') byVariant[e.variant].conversions++;
      });
      return byVariant;
    } catch {
      return null;
    }
  }, []);

  return { variant, setManualVariant, trackEvent, getAnalytics };
}
